import { useTranslation } from 'react-i18next';
import { TextInput, Button, ActionIcon } from '@mantine/core';
import { IconPhone, IconWorld, IconTrash, IconPlus } from '@tabler/icons-react';
import { Business } from '@turizi/shared';
import CountrySelector from '@/components/country-selector';
import { useState, useEffect } from 'react';
import { localTenant } from '@/services/session-service';
import type { UseMutationResult } from '@tanstack/react-query';

interface BusinessContactProps {
    business: Business;
    updateMutation: UseMutationResult<any, Error, Partial<Business>, unknown>;
}

export default function BusinessContact({
    business,
    updateMutation
}: BusinessContactProps): JSX.Element {
    const { t } = useTranslation();
    const tenant = localTenant();

    // Permissions
    const isOwner = tenant && business && business.owner_id === tenant.id;
    const isManager = tenant && business && business.manager_id === tenant.id;
    const canEditBusiness = isOwner || isManager;

    // State for Business editing
    const [businessDraft, setBusinessDraft] = useState<Business>(business);
    const [businessBackup, setBusinessBackup] = useState<Business>(business);

    // Loading states
    const [savingSections, setSavingSections] = useState<Record<string, boolean>>({});

    // Initialize state when data loads
    useEffect(() => {
        setBusinessDraft(business);
        setBusinessBackup(business);
    }, [business]);

    const saveBusinessContact = () => {
        setSavingSections(prev => ({ ...prev, contact: true }));
        updateMutation.mutate(businessDraft, {
            onSuccess: () => {
                setSavingSections(prev => ({ ...prev, contact: false }));
                setBusinessBackup({ ...businessDraft });
            },
            onError: () => {
                setSavingSections(prev => ({ ...prev, contact: false }));
            }
        });
    };

    const saveBusinessLocation = () => {
        setSavingSections(prev => ({ ...prev, location: true }));
        updateMutation.mutate(businessDraft, {
            onSuccess: () => {
                setSavingSections(prev => ({ ...prev, location: false }));
                setBusinessBackup({ ...businessDraft });
            },
            onError: () => {
                setSavingSections(prev => ({ ...prev, location: false }));
            }
        });
    };

    // Check for changes
    const hasBusinessContactChanges = () => {
        return (
            JSON.stringify(businessDraft.phones) !== JSON.stringify(businessBackup.phones)
        );
    };

    const hasBusinessLocationChanges = () => {
        return (
            businessDraft.postal_code !== businessBackup.postal_code ||
            businessDraft.address !== businessBackup.address ||
            businessDraft.city !== businessBackup.city ||
            businessDraft.province !== businessBackup.province ||
            businessDraft.country_code !== businessBackup.country_code
        );
    };

    return (
        <div className="space-y-4">
            <div className="solid-box-border">
                <div className="text-md font-bold">
                    {t('phones')}
                </div>
                <div className="flex flex-col gap-1">
                    <div className="flex justify-between items-center mt-4">
                        {canEditBusiness && (
                            <ActionIcon
                                variant="light" color="black" size="sm" radius="md"
                                onClick={() => {
                                    const newKey = `phone_${Object.keys(businessDraft.phones || {}).length + 1}`;
                                    setBusinessDraft({
                                        ...businessDraft,
                                        phones: { ...businessDraft.phones, [newKey]: '' }
                                    });
                                }}
                            >
                                <IconPlus size={14} />
                            </ActionIcon>
                        )}
                    </div>
                    {Object.entries(businessDraft.phones || {}).map(([key, value], index) => (
                        <div key={`phone-${index}`} className="flex gap-2 items-center">
                            <TextInput
                                placeholder={t('label')}
                                className="w-1/3"
                                value={key || ''}
                                onChange={(e) => {
                                    const newKey = e.target.value;
                                    if (newKey && newKey !== key) {
                                        const { [key]: oldVal, ...rest } = businessDraft.phones;
                                        setBusinessDraft({
                                            ...businessDraft,
                                            phones: { ...rest, [newKey]: value }
                                        });
                                    }
                                }}
                                disabled={!canEditBusiness}
                            />
                            <TextInput
                                placeholder={t('phone')}
                                className="flex-1"
                                leftSection={<IconPhone size={16} />}
                                value={value || ''}
                                onChange={(e) => {
                                    setBusinessDraft({
                                        ...businessDraft,
                                        phones: { ...businessDraft.phones, [key]: e.target.value }
                                    });
                                }}
                                disabled={!canEditBusiness}
                            />
                            {canEditBusiness && (
                                <ActionIcon
                                    color="red" variant="transparent"
                                    onClick={() => {
                                        const { [key]: deleted, ...rest } = businessDraft.phones;
                                        setBusinessDraft({ ...businessDraft, phones: rest });
                                    }}
                                >
                                    <IconTrash size={16} />
                                </ActionIcon>
                            )}
                        </div>
                    ))}
                </div>
                {canEditBusiness && (
                    <div className="flex justify-end mt-4">
                        <Button
                            size="xs"
                            radius="xl"
                            color="black"
                            onClick={saveBusinessContact}
                            loading={savingSections.contact}
                            disabled={!hasBusinessContactChanges()}
                        >
                            {t('save')}
                        </Button>
                    </div>
                )}
            </div>

            <div className="solid-box-border flex-1 h-fit">
                <div className="text-md font-bold mb-4">
                    {t('location')}
                </div>
                <div className="flex flex-col gap-2">
                    <TextInput
                        className='flex-1'
                        label={t('address')}
                        value={businessDraft.address || ''}
                        onChange={(e) => setBusinessDraft({ ...businessDraft, address: e.target.value })}
                    />
                    <TextInput
                        className='flex-1'
                        label={t('city')}
                        value={businessDraft.city || ''}
                        onChange={(e) => setBusinessDraft({ ...businessDraft, city: e.target.value })}
                    />
                    <TextInput
                        className='flex-1'
                        label={t('province')}
                        value={businessDraft.province || ''}
                        onChange={(e) => setBusinessDraft({ ...businessDraft, province: e.target.value })}
                    />
                    <CountrySelector
                        className='flex-1'
                        value={businessDraft.country_code || ''}
                        onChange={(value) => setBusinessDraft({ ...businessDraft, country_code: value || '' })}
                    />
                    <TextInput
                        className='flex-1'
                        label={t('postal_code')}
                        value={businessDraft.postal_code || ''}
                        onChange={(e) => setBusinessDraft({ ...businessDraft, postal_code: e.target.value })}
                    />
                </div>
                {canEditBusiness && (
                    <div className="flex justify-end mt-4">
                        <Button
                            size="xs"
                            radius="xl"
                            color="black"
                            onClick={saveBusinessLocation}
                            disabled={!hasBusinessLocationChanges()}
                            loading={savingSections.location}
                        >
                            {t('save')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
